import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { BusinessSettingsLayout } from '@components/layout/BusinessSettingsLayout';
import { BrandLoader } from '@components/ui/BrandLoader';
import { mapChartTemplateError, useSystemChartTemplates } from '@features/chartTemplates';
import type { ChartTemplate } from '@features/chartTemplates';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "massage-therapy" → "Massage therapy" for display. */
function formatSpecialty(tag: string): string {
  const spaced = tag.replace(/[-_]/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function TemplateIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <path d="M14 2v6h6M9 13h6M9 17h6M9 9h1" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

// ─── Template row ─────────────────────────────────────────────────────────────

function ChartTemplateRow({ template, onView }: { template: ChartTemplate; onView: () => void }) {
  const { t } = useTranslation();

  return (
    <li
      className="group flex cursor-pointer flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5 transition-colors hover:bg-muted/30 sm:px-6"
      role="button"
      tabIndex={0}
      onClick={onView}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onView();
        }
      }}
    >
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand/10 text-brand"
        aria-hidden="true"
      >
        <TemplateIcon />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="truncate text-sm font-medium text-foreground">{template.name}</span>
          {template.specialtyTag && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {formatSpecialty(template.specialtyTag)}
            </span>
          )}
        </div>
        {template.description && (
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{template.description}</p>
        )}
      </div>

      <span className="hidden w-28 shrink-0 text-xs text-muted-foreground sm:block">
        {template.activeVersion !== null
          ? t('chartTemplates.versionActive', { version: template.activeVersion })
          : t('chartTemplates.versionNone')}
      </span>

      <span className="hidden w-24 shrink-0 text-xs tabular-nums text-muted-foreground md:block">
        {formatDate(template.updatedAt)}
      </span>

      <span className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors group-hover:text-brand">
        {t('chartTemplates.viewAction')}
        <ChevronRightIcon />
      </span>
    </li>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ChartTemplatesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { templates, isLoading, fetchError } = useSystemChartTemplates();

  return (
    <BusinessSettingsLayout>
      {isLoading ? (
        <BrandLoader
          title={t('chartTemplates.loading.title')}
          subtitle={t('chartTemplates.loading.subtitle')}
        />
      ) : fetchError ? (
        <div className="rounded-md border border-border bg-surface px-6 py-12 text-center">
          <p className="text-sm text-destructive">
            {mapChartTemplateError(fetchError, t, 'chartTemplates.errors.loadFailed')}
          </p>
        </div>
      ) : (
        <div className="rounded-md border border-border bg-surface">
          {/* Header */}
          <div className="border-b border-border px-4 py-3 sm:px-6 sm:py-4">
            <h2 className="text-base font-semibold text-foreground">{t('chartTemplates.title')}</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {t('chartTemplates.description')}
            </p>
          </div>

          {/* List */}
          {templates.length === 0 ? (
            <p className="px-6 py-12 text-center text-sm text-muted-foreground">
              {t('chartTemplates.empty')}
            </p>
          ) : (
            <ul className="divide-y divide-border/60">
              {templates.map((template) => (
                <ChartTemplateRow
                  key={template.id}
                  template={template}
                  onView={() => navigate(`/account/settings/chart-templates/${template.id}`)}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </BusinessSettingsLayout>
  );
}
